import React from 'react';

import {
  DISPLAY,
  FLEX,
  RADIUS_SCALE,
  TYPE_SCALE,
  FONT_WEIGHTS,
  TRANSITION,
  PADDING_X,
  PADDING_Y,
  MARGIN_LEFT,
  SURFACE_PADDING,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

// Status configurations for StatusBadge
const STATUS_CONFIGS = {
  online: {
    variant: 'success' as const,
    dotColor: 'bg-green-500',
    label: 'Online',
    baseAnimate: '',
  },
  offline: {
    variant: 'secondary' as const,
    dotColor: 'bg-gray-400',
    label: 'Offline',
    baseAnimate: '',
  },
  away: { variant: 'warning' as const, dotColor: 'bg-yellow-500', label: 'Away', baseAnimate: '' },
  busy: { variant: 'error' as const, dotColor: 'bg-red-500', label: 'Busy', baseAnimate: '' },
  pending: {
    variant: 'info' as const,
    dotColor: 'bg-blue-500',
    label: 'Pending',
    baseAnimate: 'animate-pulse',
  },
  completed: {
    variant: 'success' as const,
    dotColor: 'bg-green-500',
    label: 'Completed',
    baseAnimate: 'animate-bounce',
  },
  error: {
    variant: 'error' as const,
    dotColor: 'bg-red-500',
    label: 'Error',
    baseAnimate: 'animate-pulse',
  },
  warning: {
    variant: 'warning' as const,
    dotColor: 'bg-yellow-500',
    label: 'Warning',
    baseAnimate: 'animate-pulse',
  },
  info: { variant: 'info' as const, dotColor: 'bg-blue-500', label: 'Info', baseAnimate: '' },
  success: {
    variant: 'success' as const,
    dotColor: 'bg-green-500',
    label: 'Success',
    baseAnimate: '',
  },
} as const;

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'outline' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  children: React.ReactNode;
  animate?: boolean;
}

export function Badge({
  variant = 'default',
  size = 'md',
  interactive = false,
  removable = false,
  onRemove,
  className,
  children,
  animate = false,
  ...props
}: BadgeProps) {
  const getVariantClasses = (variant: BadgeProps['variant']) => {
    switch (variant) {
      case 'default':
        return 'bg-primary text-primary-foreground hover:bg-primary/90';
      case 'outline':
        return 'border border-input bg-background hover:bg-accent hover:text-accent-foreground';
      case 'secondary':
        return 'bg-secondary text-secondary-foreground hover:bg-secondary/80';
      case 'success':
        return 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border border-red-200 hover:bg-red-200';
      case 'info':
        return 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200';
      default:
        return 'bg-primary text-primary-foreground hover:bg-primary/90';
    }
  };

  const getSizeClasses = (size: BadgeProps['size']) => {
    switch (size) {
      case 'sm':
        return `${PADDING_X['2']} ${PADDING_Y['0.5']} ${TYPE_SCALE['2xs']}`;
      case 'lg':
        return `${PADDING_X['3']} ${PADDING_Y['1']} ${TYPE_SCALE.sm}`;
      default:
        return `${PADDING_X['2.5']} ${PADDING_Y['0.5']} ${TYPE_SCALE.xs}`;
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove?.();
  };

  return (
    <span
      className={cn(
        DISPLAY.inlineFlex,
        FLEX.flexItemsCenter,
        RADIUS_SCALE.full,
        FONT_WEIGHTS.medium,
        TRANSITION.colors,
        getVariantClasses(variant),
        getSizeClasses(size),
        interactive && 'cursor-pointer hover:scale-105 active:scale-95',
        animate && 'animate-in zoom-in-95 duration-200',
        removable && 'pr-1',
        className
      )}
      {...props}
    >
      {children}
      {removable && (
        <button
          onClick={handleRemove}
          className={`${MARGIN_LEFT['1']} ${SURFACE_PADDING['0.5']} ${RADIUS_SCALE.full} hover:bg-black/10 ${TRANSITION.colors}`}
          aria-label="Remove badge"
        >
          <span className={`text-current ${TYPE_SCALE.xs}`}>×</span>
        </button>
      )}
    </span>
  );
}

// Status Badge component with animated states
interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: 'online' | 'offline' | 'away' | 'busy' | 'pending' | 'completed' | 'error' | 'warning';
  showPulse?: boolean;
  showDot?: boolean;
}

export function StatusBadge({
  status,
  showPulse = true,
  showDot = true,
  size = 'sm',
  className,
  children,
  ...props
}: StatusBadgeProps) {
  const getStatusConfig = (status: StatusBadgeProps['status']) => {
    const config = STATUS_CONFIGS[status as keyof typeof STATUS_CONFIGS];
    return {
      ...config,
      animate: config.baseAnimate || (showPulse ? 'animate-pulse' : ''),
    };
  };

  const config = getStatusConfig(status);

  return (
    <Badge variant={config.variant} size={size} className={cn('gap-1.5', className)} {...props}>
      {showDot && (
        <span
          className={cn('w-2 h-2 rounded-full flex-shrink-0', config.dotColor, config.animate)}
        />
      )}
      {children ?? config.label}
    </Badge>
  );
}
